import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams, useLocation } from '@tanstack/react-router'
import {
  Plus,
  Trash2,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff,
  Megaphone,
  ArrowUp,
  ArrowDown,
} from 'lucide-react'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { apiGet, apiPost, apiPut, apiDelete } from '../lib/api'

type Promo = {
  id: number
  texto: string
  codigoDescuento: string | null
  url: string | null
  activo: boolean
  validFrom: string | null
  validUntil: string | null
  prioridad: number
}

type Draft = {
  texto: string
  codigo_descuento: string
  url: string
  activo: boolean
  valid_from: string
  valid_until: string
  prioridad: number
}

const EMPTY_DRAFT: Draft = {
  texto: '',
  codigo_descuento: '',
  url: '',
  activo: true,
  valid_from: '',
  valid_until: '',
  prioridad: 0,
}

export function PromoBar() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams({ strict: false }) as { id?: string }
  const [flash, setFlash] = useState<{ tipo: 'ok' | 'err'; msg: string } | null>(null)
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT)
  const [editingId, setEditingId] = useState<number | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['promo-bar'],
    queryFn: () => apiGet<{ data: Promo[] }>('/api/admin/promo-bar'),
  })

  const items = data?.data ?? []

  // Sync state to URL: /promo-bar/nuevo -> create, /promo-bar/:id -> highlight, /promo-bar -> list
  useEffect(() => {
    if (location.pathname === '/promo-bar/nuevo') {
      setAdding(true)
      setEditingId(null)
    } else if (params.id) {
      const idNum = Number(params.id)
      setEditingId(Number.isFinite(idNum) ? idNum : null)
      setAdding(false)
    } else {
      setEditingId(null)
    }
  }, [location.pathname, params.id])

  function notify(tipo: 'ok' | 'err', msg: string) {
    setFlash({ tipo, msg })
    setTimeout(() => setFlash(null), 3000)
  }

  function refresh() {
    qc.invalidateQueries({ queryKey: ['promo-bar'] })
  }

  const createMut = useMutation({
    mutationFn: (body: {
      texto: string
      codigo_descuento: string | null
      url: string | null
      activo: boolean
      valid_from: string | null
      valid_until: string | null
      prioridad: number
    }) => apiPost<{ id: number }>('/api/admin/promo-bar', body),
    onSuccess: () => {
      notify('ok', 'Promo creada')
      setAdding(false)
      setDraft(EMPTY_DRAFT)
      refresh()
      navigate({ to: '/promo-bar' })
    },
    onError: (e: Error) => notify('err', e.message),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, ...body }: { id: number } & Record<string, unknown>) =>
      apiPut<{ ok: true }>(`/api/admin/promo-bar/${id}`, body),
    onSuccess: () => refresh(),
    onError: (e: Error) => notify('err', e.message),
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => apiDelete<{ ok: true }>(`/api/admin/promo-bar/${id}`),
    onSuccess: () => {
      notify('ok', 'Promo eliminada')
      refresh()
    },
    onError: (e: Error) => notify('err', e.message),
  })

  function confirmAdd() {
    if (!draft.texto.trim()) {
      notify('err', 'Falta el texto')
      return
    }
    createMut.mutate({
      texto: draft.texto.trim(),
      codigo_descuento: draft.codigo_descuento.trim() || null,
      url: draft.url.trim() || null,
      activo: draft.activo,
      valid_from: draft.valid_from ? new Date(draft.valid_from).toISOString() : null,
      valid_until: draft.valid_until ? new Date(draft.valid_until).toISOString() : null,
      prioridad: Number(draft.prioridad) || 0,
    })
  }

  function bumpPriority(p: Promo, dir: -1 | 1) {
    updateMut.mutate({ id: p.id, prioridad: (p.prioridad ?? 0) + dir })
  }

  return (
    <>
      <Topbar
        title="Promo bar"
        subtitle="Texto promocional que aparece en el top bar del header (autoadministrable, soporta código y URL)"
        actions={
          <>
            {data && <Badge variant="info">{items.length} promos</Badge>}
            <button
              type="button"
              onClick={() => navigate({ to: '/promo-bar/nuevo' })}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
            >
              <Plus className="size-3.5" />
              Nueva promo
            </button>
          </>
        }
      />

      {flash && (
        <div
          className={`mx-6 mt-4 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
            flash.tipo === 'ok'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {flash.tipo === 'ok' ? (
            <CheckCircle2 className="size-4" />
          ) : (
            <AlertCircle className="size-4" />
          )}
          <span>{flash.msg}</span>
        </div>
      )}

      <div className="p-6">
        {isLoading ? (
          <div className="flex items-center gap-2 text-ink-500">
            <Loader2 className="size-4 animate-spin" /> Cargando promos…
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-ink-500">
              Solo se muestra <strong>una</strong> promo en el header: la activa con mayor prioridad
              y dentro de la ventana de fechas. Si una promo no tiene fechas, se considera vigente.
            </p>

            {items.map((p) => (
              <div
                key={p.id}
                className={`rounded-2xl border bg-white p-4 shadow-sm ${
                  editingId === p.id
                    ? 'border-brand-500 ring-2 ring-brand-200'
                    : 'border-ink-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="grid size-10 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-600">
                    <Megaphone className="size-5" />
                  </div>

                  <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-12">
                    <input
                      type="text"
                      value={p.texto}
                      placeholder="Texto"
                      onChange={(e) =>
                        updateMut.mutate({ id: p.id, texto: e.target.value })
                      }
                      className="sm:col-span-6 rounded-md border border-ink-200 bg-white px-2 py-1.5 text-sm font-semibold outline-none focus:border-brand-500"
                    />
                    <input
                      type="text"
                      value={p.codigoDescuento ?? ''}
                      placeholder="Código (opcional)"
                      onChange={(e) =>
                        updateMut.mutate({
                          id: p.id,
                          codigo_descuento: e.target.value || null,
                        })
                      }
                      className="sm:col-span-2 rounded-md border border-ink-200 bg-white px-2 py-1.5 text-sm outline-none focus:border-brand-500"
                    />
                    <input
                      type="text"
                      value={p.url ?? ''}
                      placeholder="URL destino (opcional)"
                      onChange={(e) =>
                        updateMut.mutate({ id: p.id, url: e.target.value || null })
                      }
                      className="sm:col-span-4 rounded-md border border-ink-200 bg-white px-2 py-1.5 text-sm outline-none focus:border-brand-500"
                    />
                    <input
                      type="datetime-local"
                      value={p.validFrom ? p.validFrom.slice(0, 16) : ''}
                      onChange={(e) =>
                        updateMut.mutate({
                          id: p.id,
                          valid_from: e.target.value
                            ? new Date(e.target.value).toISOString()
                            : null,
                        })
                      }
                      className="sm:col-span-4 rounded-md border border-ink-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-brand-500"
                    />
                    <input
                      type="datetime-local"
                      value={p.validUntil ? p.validUntil.slice(0, 16) : ''}
                      onChange={(e) =>
                        updateMut.mutate({
                          id: p.id,
                          valid_until: e.target.value
                            ? new Date(e.target.value).toISOString()
                            : null,
                        })
                      }
                      className="sm:col-span-4 rounded-md border border-ink-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-brand-500"
                    />
                    <div className="sm:col-span-4 flex items-center gap-1 text-xs text-ink-500">
                      <span>Prio:</span>
                      <strong className="text-ink-900">{p.prioridad}</strong>
                      <button
                        type="button"
                        onClick={() => bumpPriority(p, 1)}
                        className="grid size-7 place-items-center rounded text-ink-500 hover:bg-ink-100"
                      >
                        <ArrowUp className="size-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => bumpPriority(p, -1)}
                        className="grid size-7 place-items-center rounded text-ink-500 hover:bg-ink-100"
                      >
                        <ArrowDown className="size-3.5" />
                      </button>
                    </div>
                  </div>

                  <button
                    type="button"
                    title={p.activo ? 'Desactivar' : 'Activar'}
                    onClick={() => updateMut.mutate({ id: p.id, activo: !p.activo })}
                    className="grid size-8 place-items-center rounded text-ink-500 hover:bg-ink-100"
                  >
                    {p.activo ? (
                      <Eye className="size-4 text-emerald-600" />
                    ) : (
                      <EyeOff className="size-4 text-ink-400" />
                    )}
                  </button>
                  <button
                    type="button"
                    title="Eliminar"
                    onClick={() => {
                      if (confirm(`Eliminar promo "${p.texto}"?`)) deleteMut.mutate(p.id)
                    }}
                    className="grid size-8 place-items-center rounded text-red-500 hover:bg-red-50"
                  >
                    <Trash2 className="size-4" />
                  </button>
                </div>
              </div>
            ))}

            {adding && (
              <div className="rounded-2xl border border-dashed border-brand-300 bg-brand-50 p-4">
                <div className="grid gap-2 sm:grid-cols-12">
                  <input
                    type="text"
                    autoFocus
                    value={draft.texto}
                    placeholder="Texto (ej: OFERTA RELÁMPAGO -60% en baterías)"
                    onChange={(e) => setDraft((d) => ({ ...d, texto: e.target.value }))}
                    className="sm:col-span-6 rounded-md border border-ink-200 bg-white px-2 py-1.5 text-sm font-semibold outline-none focus:border-brand-500"
                  />
                  <input
                    type="text"
                    value={draft.codigo_descuento}
                    placeholder="Código (opcional)"
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, codigo_descuento: e.target.value }))
                    }
                    className="sm:col-span-2 rounded-md border border-ink-200 bg-white px-2 py-1.5 text-sm outline-none focus:border-brand-500"
                  />
                  <input
                    type="text"
                    value={draft.url}
                    placeholder="URL destino (opcional)"
                    onChange={(e) => setDraft((d) => ({ ...d, url: e.target.value }))}
                    className="sm:col-span-4 rounded-md border border-ink-200 bg-white px-2 py-1.5 text-sm outline-none focus:border-brand-500"
                  />
                  <input
                    type="datetime-local"
                    value={draft.valid_from}
                    onChange={(e) => setDraft((d) => ({ ...d, valid_from: e.target.value }))}
                    className="sm:col-span-4 rounded-md border border-ink-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-brand-500"
                  />
                  <input
                    type="datetime-local"
                    value={draft.valid_until}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, valid_until: e.target.value }))
                    }
                    className="sm:col-span-4 rounded-md border border-ink-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-brand-500"
                  />
                  <input
                    type="number"
                    value={draft.prioridad}
                    placeholder="Prioridad"
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, prioridad: Number(e.target.value) || 0 }))
                    }
                    className="sm:col-span-2 rounded-md border border-ink-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-brand-500"
                  />
                  <label className="sm:col-span-2 inline-flex items-center gap-2 text-xs text-ink-600">
                    <input
                      type="checkbox"
                      checked={draft.activo}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, activo: e.target.checked }))
                      }
                    />
                    Activo
                  </label>
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setAdding(false)
                      setDraft(EMPTY_DRAFT)
                      navigate({ to: '/promo-bar' })
                    }}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1.5 text-xs text-ink-600 hover:bg-ink-100"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={confirmAdd}
                    className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700"
                  >
                    Crear promo
                  </button>
                </div>
              </div>
            )}

            {!isLoading && items.length === 0 && !adding && (
              <div className="rounded-2xl border border-dashed border-ink-200 bg-white p-10 text-center">
                <h3 className="text-sm font-semibold text-ink-900">Sin promos</h3>
                <p className="mt-1 text-xs text-ink-500">
                  Crea la primera con el botón de arriba.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}
